import { Position, WallSegment, Zone, ZoneType } from '../types/maze-types';

/**
 * Available maze shapes
 */
export type MazeShape = 'rectangle' | 'circle' | 'star' | 'diamond' | 'hexagon' | 'oval' | 'square';

/**
 * Get random maze shape
 */
export function getRandomShape(): MazeShape {
  const shapes: MazeShape[] = ['rectangle', 'circle', 'star', 'diamond', 'hexagon', 'oval', 'square'];
  return shapes[Math.floor(Math.random() * shapes.length)];
}

/**
 * Check if point is inside shape (normalized coordinates from -1 to 1)
 */
function isPointInShape(nx: number, ny: number, shape: MazeShape): boolean {
  switch (shape) {
    case 'rectangle':
      return Math.abs(nx) <= 0.9 && Math.abs(ny) <= 0.9;

    case 'square': {
      const size = Math.min(0.9, 0.9);
      return Math.abs(nx) <= size && Math.abs(ny) <= size;
    }

    case 'circle':
      return nx * nx + ny * ny <= 0.81; // 0.9^2

    case 'oval':
      return (nx * nx) / (0.9 * 0.9) + (ny * ny) / (0.7 * 0.7) <= 1;

    case 'diamond':
      return Math.abs(nx) / 0.9 + Math.abs(ny) / 0.9 <= 1;

    case 'hexagon': {
      const angle = Math.atan2(ny, nx);
      const r = Math.sqrt(nx * nx + ny * ny);
      const sideAngle = Math.PI / 3;
      const cornerAngle = ((angle % sideAngle) + sideAngle) % sideAngle;
      const maxR = 0.85 / Math.cos(cornerAngle - sideAngle / 2);
      return r <= maxR;
    }

    case 'star': {
      const angle = Math.atan2(ny, nx);
      const r = Math.sqrt(nx * nx + ny * ny);
      const points = 5;
      const pointAngle = (Math.PI * 2) / points;
      const angleInPoint = ((angle + Math.PI) % pointAngle) - pointAngle / 2;
      const innerRadius = 0.4;
      const outerRadius = 0.85;
      const radiusAtAngle = innerRadius + (outerRadius - innerRadius) *
        (0.5 + 0.5 * Math.cos(angleInPoint * 2 * points));
      return r <= radiusAtAngle;
    }

    default:
      return true;
  }
}

interface Cell {
  x: number;
  y: number;
  walls: {
    top: boolean;
    right: boolean;
    bottom: boolean;
    left: boolean;
  };
}

/**
 * Generate simple maze with shape boundary using DFS
 */
export function generateShapedMaze(
  canvasWidth: number,
  canvasHeight: number,
  cellSize: number,
  shape: MazeShape,
  complexity: number
): {
  walls: WallSegment[];
  start: Position;
  end: Position;
  entranceZone: Zone;
  exitZone: Zone;
} {
  const cols = Math.floor(canvasWidth / cellSize);
  const rows = Math.floor(canvasHeight / cellSize);
  const centerX = cols / 2;
  const centerY = rows / 2;


  // Create grid: true = valid cell inside shape, false = outside
  const insideShape: boolean[][] = [];
  const cells: (Cell | null)[][] = [];

  for (let y = 0; y < rows; y++) {
    insideShape[y] = [];
    cells[y] = [];
    for (let x = 0; x < cols; x++) {
      // Normalize coordinates to [-1, 1]
      const nx = (x - centerX) / (cols / 2);
      const ny = (y - centerY) / (rows / 2);
      const inside = isPointInShape(nx, ny, shape);
      insideShape[y][x] = inside;

      if (inside) {
        cells[y][x] = {
          x,
          y,
          walls: { top: true, right: true, bottom: true, left: true }
        };
      } else {
        cells[y][x] = null;
      }
    }
  }

  // Find valid start and end positions well within shape bounds
  let startX = -1, startY = -1, endX = -1, endY = -1;

  // Calculate zone size first to ensure proper margin
  const zoneSize = Math.max(1, Math.min(3, Math.floor(Math.min(cols, rows) / 15)));

  // Find start in top-left quadrant (with margin from edges)
  // Margin must account for the full clear radius around the emoji
  const emojiClearRadius = Math.max(2, zoneSize);
  const marginCells = Math.max(emojiClearRadius + 1, Math.floor(Math.min(cols, rows) / 10));

  // Check that the entire emoji clear area fits inside the shape
  const fitsInShape = (cx: number, cy: number): boolean => {
    for (let dy = -emojiClearRadius; dy <= emojiClearRadius; dy++) {
      for (let dx = -emojiClearRadius; dx <= emojiClearRadius; dx++) {
        const checkY = cy + dy;
        const checkX = cx + dx;
        if (checkX < 0 || checkY < 0 || checkY >= rows || checkX >= cols || !insideShape[checkY][checkX]) {
          return false;
        }
      }
    }
    return true;
  };

  // Collect all valid entrance positions in top-left quadrant
  const validEntrancePositions: Array<{x: number, y: number}> = [];
  for (let y = marginCells; y < Math.floor(rows / 2); y++) {
    for (let x = marginCells; x < Math.floor(cols / 2); x++) {
      if (fitsInShape(x, y)) {
        validEntrancePositions.push({x, y});
      }
    }
  }

  // Randomly select entrance position from valid options
  if (validEntrancePositions.length > 0) {
    const randomEntrance = validEntrancePositions[Math.floor(Math.random() * validEntrancePositions.length)];
    startX = randomEntrance.x;
    startY = randomEntrance.y;
  }

  // Collect all valid exit positions in bottom-right quadrant
  const validExitPositions: Array<{x: number, y: number}> = [];
  for (let y = rows - 1 - marginCells; y >= Math.floor(rows / 2); y--) {
    for (let x = cols - 1 - marginCells; x >= Math.floor(cols / 2); x--) {
      if (fitsInShape(x, y)) {
        validExitPositions.push({x, y});
      }
    }
  }

  // Randomly select exit position from valid options
  if (validExitPositions.length > 0) {
    const randomExit = validExitPositions[Math.floor(Math.random() * validExitPositions.length)];
    endX = randomExit.x;
    endY = randomExit.y;
  }

  // Fallback if no valid positions found - use single cells
  if (startX === -1 || endX === -1) {
    for (let y = 0; y < rows; y++) {
      for (let x = 0; x < cols; x++) {
        if (insideShape[y][x]) {
          // For fallback, just use single cell positions
          if (startX === -1) { startX = x; startY = y; }
          endX = x;
          endY = y;
        }
      }
    }
  }

  // Generate maze using DFS - this removes walls
  const visited: boolean[][] = Array(rows).fill(0).map(() => Array(cols).fill(false));
  const stack: [number, number][] = [[startX, startY]];
  visited[startY][startX] = true;

  const directions = [
    { dx: 0, dy: -1, dir: 'top', opposite: 'bottom' },
    { dx: 1, dy: 0, dir: 'right', opposite: 'left' },
    { dx: 0, dy: 1, dir: 'bottom', opposite: 'top' },
    { dx: -1, dy: 0, dir: 'left', opposite: 'right' }
  ];

  while (stack.length > 0) {
    const [x, y] = stack[stack.length - 1];
    const currentCell = cells[y][x];
    if (!currentCell) {
      stack.pop();
      continue;
    }

    // Get unvisited neighbors inside shape
    const neighbors: Array<typeof directions[0] & { nx: number; ny: number }> = [];
    for (const dir of directions) {
      const nx = x + dir.dx;
      const ny = y + dir.dy;
      if (nx >= 0 && nx < cols && ny >= 0 && ny < rows &&
          !visited[ny][nx] && insideShape[ny][nx] && cells[ny][nx]) {
        neighbors.push({ ...dir, nx, ny });
      }
    }

    if (neighbors.length > 0) {
      // Choose random neighbor
      const chosen = neighbors[Math.floor(Math.random() * neighbors.length)];

      // Remove walls between current and chosen cell
      (currentCell.walls as any)[chosen.dir] = false;
      const neighborCell = cells[chosen.ny][chosen.nx];
      if (neighborCell) {
        (neighborCell.walls as any)[chosen.opposite] = false;
      }

      visited[chosen.ny][chosen.nx] = true;
      stack.push([chosen.nx, chosen.ny]);

      // Additional branching based on complexity
      if (Math.random() < complexity && neighbors.length > 1) {
        const chosen2 = neighbors[Math.floor(Math.random() * neighbors.length)];
        if (!visited[chosen2.ny][chosen2.nx]) {
          (currentCell.walls as any)[chosen2.dir] = false;
          const neighborCell2 = cells[chosen2.ny][chosen2.nx];
          if (neighborCell2) {
            (neighborCell2.walls as any)[chosen2.opposite] = false;
          }
          visited[chosen2.ny][chosen2.nx] = true;
          stack.push([chosen2.nx, chosen2.ny]);
        }
      }
    } else {
      stack.pop();
    }
  }

  // Generate wall segments from cell walls
  const walls: WallSegment[] = [];

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const cell = cells[y][x];
      if (!cell) continue;

      const cellX = x * cellSize;
      const cellY = y * cellSize;

      // Top wall
      if (cell.walls.top) {
        walls.push({
          type: 'line',
          start: { x: cellX, y: cellY },
          end: { x: cellX + cellSize, y: cellY }
        });
      }

      // Right wall
      if (cell.walls.right) {
        walls.push({
          type: 'line',
          start: { x: cellX + cellSize, y: cellY },
          end: { x: cellX + cellSize, y: cellY + cellSize }
        });
      }

      // Bottom wall
      if (cell.walls.bottom) {
        walls.push({
          type: 'line',
          start: { x: cellX, y: cellY + cellSize },
          end: { x: cellX + cellSize, y: cellY + cellSize }
        });
      }

      // Left wall
      if (cell.walls.left) {
        walls.push({
          type: 'line',
          start: { x: cellX, y: cellY },
          end: { x: cellX, y: cellY + cellSize }
        });
      }
    }
  }

  // Helper function to check if a cell is on the boundary of the shape
  const isBoundaryCell = (x: number, y: number): boolean => {
    if (!insideShape[y][x]) return false;

    // Check if any neighbor is outside the shape
    const neighbors = [
      { dx: 0, dy: -1 }, // top
      { dx: 1, dy: 0 },  // right
      { dx: 0, dy: 1 },  // bottom
      { dx: -1, dy: 0 }  // left
    ];

    for (const { dx, dy } of neighbors) {
      const nx = x + dx;
      const ny = y + dy;
      // If neighbor is out of bounds or outside shape, this is a boundary cell
      if (nx < 0 || nx >= cols || ny < 0 || ny >= rows || !insideShape[ny][nx]) {
        return true;
      }
    }

    return false;
  };

  // Clear walls around entrance and exit zones for emoji visibility
  // But preserve boundary walls to maintain shape integrity
  // Use a larger clear radius so emoji has room to render
  const zoneClearRadius = Math.max(2, zoneSize);

  // Clear walls around start (but keep boundary walls)
  for (let dy = -zoneClearRadius; dy <= zoneClearRadius; dy++) {
    for (let dx = -zoneClearRadius; dx <= zoneClearRadius; dx++) {
      const cx = startX + dx;
      const cy = startY + dy;
      if (cx >= 0 && cx < cols && cy >= 0 && cy < rows && cells[cy][cx]) {
        const cell = cells[cy][cx];

        // Only clear interior walls, preserve boundary walls
        if (!isBoundaryCell(cx, cy)) {
          cell.walls = { top: false, right: false, bottom: false, left: false };
        } else {
          // For boundary cells, only clear walls that don't face outside
          if (cy > 0 && insideShape[cy - 1][cx]) cell.walls.top = false;
          if (cx < cols - 1 && insideShape[cy][cx + 1]) cell.walls.right = false;
          if (cy < rows - 1 && insideShape[cy + 1][cx]) cell.walls.bottom = false;
          if (cx > 0 && insideShape[cy][cx - 1]) cell.walls.left = false;
        }
      }
    }
  }

  // Clear walls around end (but keep boundary walls)
  for (let dy = -zoneClearRadius; dy <= zoneClearRadius; dy++) {
    for (let dx = -zoneClearRadius; dx <= zoneClearRadius; dx++) {
      const cx = endX + dx;
      const cy = endY + dy;
      if (cx >= 0 && cx < cols && cy >= 0 && cy < rows && cells[cy][cx]) {
        const cell = cells[cy][cx];

        // Only clear interior walls, preserve boundary walls
        if (!isBoundaryCell(cx, cy)) {
          cell.walls = { top: false, right: false, bottom: false, left: false };
        } else {
          // For boundary cells, only clear walls that don't face outside
          if (cy > 0 && insideShape[cy - 1][cx]) cell.walls.top = false;
          if (cx < cols - 1 && insideShape[cy][cx + 1]) cell.walls.right = false;
          if (cy < rows - 1 && insideShape[cy + 1][cx]) cell.walls.bottom = false;
          if (cx > 0 && insideShape[cy][cx - 1]) cell.walls.left = false;
        }
      }
    }
  }

  // Re-generate wall segments after clearing zones
  const clearedWalls: WallSegment[] = [];

  for (let y = 0; y < rows; y++) {
    for (let x = 0; x < cols; x++) {
      const cell = cells[y][x];
      if (!cell) continue;

      const cellX = x * cellSize;
      const cellY = y * cellSize;

      // Top wall
      if (cell.walls.top) {
        clearedWalls.push({
          type: 'line',
          start: { x: cellX, y: cellY },
          end: { x: cellX + cellSize, y: cellY }
        });
      }

      // Right wall
      if (cell.walls.right) {
        clearedWalls.push({
          type: 'line',
          start: { x: cellX + cellSize, y: cellY },
          end: { x: cellX + cellSize, y: cellY + cellSize }
        });
      }

      // Bottom wall
      if (cell.walls.bottom) {
        clearedWalls.push({
          type: 'line',
          start: { x: cellX, y: cellY + cellSize },
          end: { x: cellX + cellSize, y: cellY + cellSize }
        });
      }

      // Left wall
      if (cell.walls.left) {
        clearedWalls.push({
          type: 'line',
          start: { x: cellX, y: cellY },
          end: { x: cellX, y: cellY + cellSize }
        });
      }
    }
  }

  // Create entrance and exit zones (in cell coordinates, with proper bounds)
  // Zone topLeft is offset by clearRadius so the emoji is centered on the cleared area
  const clearDiameter = zoneClearRadius * 2 + 1;
  const entranceZone: Zone = {
    topLeft: { x: startX - zoneClearRadius, y: startY - zoneClearRadius },
    size: clearDiameter,
    type: ZoneType.ENTRANCE,
    entranceCell: {
      x: startX * cellSize + cellSize / 2,
      y: startY * cellSize + cellSize / 2
    }
  };

  const exitZone: Zone = {
    topLeft: { x: endX - zoneClearRadius, y: endY - zoneClearRadius },
    size: clearDiameter,
    type: ZoneType.EXIT,
    entranceCell: {
      x: endX * cellSize + cellSize / 2,
      y: endY * cellSize + cellSize / 2
    }
  };

  const start: Position = {
    x: startX * cellSize + cellSize / 2,
    y: startY * cellSize + cellSize / 2
  };

  const end: Position = {
    x: endX * cellSize + cellSize / 2,
    y: endY * cellSize + cellSize / 2
  };


  return { walls: clearedWalls, start, end, entranceZone, exitZone };
}
